import React from "react";
import Constants from "../../core/Constants";
import strings from "../../core/translate";
import {
  ItemStatus,
  ItemType,
  WorkOrderStatus,
  canAttachPhotos,
  canEditItems,
  formatHours,
  isChoiceGroup,
  itemStatusMeta,
  itemTypeMeta,
  money,
  totalEstimated,
} from "../../shared/work-orders";

/**
 * The findings, grouped the way the garage app groups them: what has to be
 * done, what the client may skip, and the services that price the job. Each
 * item carries the client's answer, their note, the conversation about it and
 * — once the work is under way — the before/after proof.
 *
 * One finding is one line. Everything you can do to it lives at the end of
 * that line and appears when the row is pointed at, rather than as a permanent
 * second row of buttons under every item: four findings used to cost as much
 * height as their tools, and the report read as a wall of controls instead of
 * as a report.
 */

const PHOTO_KINDS = [
  { kind: "before", label: () => strings.photo_before },
  { kind: "after", label: () => strings.photo_after },
];

const Pill = ({ tone, icon, children }) => (
  <span className={`wo-pill is-${tone}`}>
    {icon && <i className={icon} />}
    {children}
  </span>
);

/**
 * One thing you can do to a finding. The label is the tooltip and the
 * accessible name — an icon alone says nothing to a screen reader, and these
 * are only visible on hover.
 */
const Act = ({ icon, label, tone, on, count, onClick }) => (
  <button
    type="button"
    className={["wo-act", tone ? `is-${tone}` : "", on ? "is-on" : ""]
      .filter(Boolean)
      .join(" ")}
    title={label}
    aria-label={label}
    onClick={onClick}
  >
    <i className={icon} />
    {count > 0 && <span className="wo-act__badge">{count}</span>}
  </button>
);

/** One photo slot: what is there, or the button to put something there. */
const PhotoSlot = ({ kind, label, photo, canWrite, busy, onUpload, onRemove }) => (
  <div className="wo-photo">
    <div className="wo-photo__frame">
      {photo ? (
        <a
          href={`${Constants.serverlink}${photo.photoPath}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={`${Constants.serverlink}${photo.photoPath}`} alt={label} />
        </a>
      ) : (
        <span className="wo-hint">{strings.photo_none}</span>
      )}
    </div>
    <div className="wo-photo__label">{label}</div>
    {canWrite && (
      <div className="wo-item__tools">
        <label className="wo-link" style={{ marginBottom: 0 }}>
          {photo ? strings.photo_replace : strings.photo_add}
          <input
            type="file"
            accept="image/*"
            hidden
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files && event.target.files[0];
              // Cleared so picking the same file twice still fires a change.
              event.target.value = "";
              if (file) onUpload(kind, file);
            }}
          />
        </label>
        {photo && (
          <button
            type="button"
            className="wo-link is-danger"
            disabled={busy}
            onClick={() => onRemove(photo)}
          >
            {strings.delete}
          </button>
        )}
      </div>
    )}
  </div>
);

const DONE_SHOWN_IN = [
  WorkOrderStatus.IN_PROGRESS,
  WorkOrderStatus.DONE,
  WorkOrderStatus.READY_TO_DELIVER,
  WorkOrderStatus.DELIVERED,
];

/**
 * Under a task: who inspected it, then its mechanic,
 * hours and warranty, and — once the work is under way — whether it is done.
 * The mechanic ticks it done in the app; the panel only reads it, as an Owner
 * does there.
 */
const TaskStrip = ({ item, status, parts = [] }) => {
  const approved = item.status === ItemStatus.APPROVED;
  const waitingPart =
    approved &&
    status === WorkOrderStatus.IN_PROGRESS &&
    !item.doneAt &&
    parts.some((part) => part.itemId === item.id && !part.checked);
  const chips = [
    // who inspected it — kept once the order is priced and sent on
    item.authorName && (
      <Pill key="author" tone="muted" icon="nc-icon nc-single-02">
        {strings.formatString(strings.by_author, item.authorName)}
      </Pill>
    ),
    item.mechanicName && (
      <Pill key="mechanic" tone="primary" icon="nc-icon nc-settings-tool-66">
        {item.mechanicName}
      </Pill>
    ),
    item.hoursNeeded !== null && item.hoursNeeded !== undefined && (
      <Pill key="hours" tone="info" icon="nc-icon nc-time-alarm">
        {strings.formatString(strings.hours_short, formatHours(item.hoursNeeded))}
      </Pill>
    ),
    item.warrantyMonths && (
      <Pill key="warranty" tone="success" icon="nc-icon nc-check-2">
        {strings.formatString(strings.months_count, item.warrantyMonths)}
      </Pill>
    ),
    approved && item.doneAt && DONE_SHOWN_IN.includes(status) && (
      <Pill key="done" tone="success" icon="nc-icon nc-check-2">
        {strings.task_done}
      </Pill>
    ),
    waitingPart && (
      <Pill key="part" tone="warning" icon="nc-icon nc-box">
        {strings.waiting_for_part}
      </Pill>
    ),
  ].filter(Boolean);
  if (chips.length === 0) return null;
  return <div className="wo-item__task">{chips}</div>;
};

class ItemNode extends React.Component {
  render() {
    const {
      item,
      depth,
      isOption,
      status,
      canWrite,
      busy,
      openPanels,
      onTogglePanel,
      onOpenThread,
      onAddSub,
      onAddOption,
      onEdit,
      onRemove,
      onUploadPhoto,
      onRemovePhoto,
      currencySymbol,
    } = this.props;

    const children = item.children ?? [];
    const choiceGroup = isChoiceGroup(item);
    // A parent can offer sub-items or a choice, never both.
    const hasPlainChildren = children.length > 0 && !choiceGroup;
    const messages = item.messages ?? [];
    const photos = item.photos ?? [];
    const editable = canWrite && canEditItems(status);
    const photoable =
      canWrite &&
      canAttachPhotos(status) &&
      item.status === ItemStatus.APPROVED;
    const panel = openPanels[item.id];
    const itemStatus = itemStatusMeta(item.status);

    return (
      <React.Fragment>
        <div
          className={[
            "wo-item",
            depth > 0 ? "is-child" : "",
            isOption ? "is-option" : "",
            isOption && item.selected ? "is-picked" : "",
            item.status === ItemStatus.REJECTED ? "is-rejected" : "",
            // An open panel keeps its own row's tools on screen.
            panel ? "is-open" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          // Indents at any depth without a class per level.
          style={{ "--wo-depth": depth }}
        >
          <div className="wo-item__row">
            <div className="wo-item__meta">
              <div className="wo-item__badges">
                {choiceGroup && <Pill tone="muted">{strings.choose_one}</Pill>}
                {isOption && item.selected && (
                  <Pill tone="success" icon="nc-icon nc-check-2">
                    {strings.picked}
                  </Pill>
                )}
                {item.autoApproveWithParent && (
                  <Pill tone="info">{strings.auto_approved}</Pill>
                )}
                {/* A choice group has no answer of its own — its options do. */}
                {!choiceGroup && (
                  <Pill tone={itemStatus.tone}>{itemStatus.label}</Pill>
                )}
              </div>

              <div className="wo-item__cost">
                {item.estimatedCost == null
                  ? "—"
                  : money(item.estimatedCost, currencySymbol)}
                {item.costPrice != null && (
                  <div className="wo-item__cost-price">
                    {strings.formatString(
                      strings.cost_price_short,
                      money(item.costPrice, currencySymbol)
                    )}
                  </div>
                )}
              </div>

              <div className="wo-item__acts">
                <Act
                  icon="nc-icon nc-chat-round"
                  label={strings.messages}
                  count={messages.length}
                  onClick={() => onOpenThread(item)}
                />
                {photoable && (
                  <Act
                    icon="nc-icon nc-camera-compact"
                    label={strings.item_photos}
                    count={photos.length}
                    on={panel === "photos"}
                    onClick={() => onTogglePanel(item.id, "photos")}
                  />
                )}
                {editable && !isOption && !choiceGroup && (
                  <Act
                    icon="nc-icon nc-simple-add"
                    label={strings.add_sub_item}
                    onClick={() => onAddSub(item)}
                  />
                )}
                {/* Hidden once the item has plain sub-items: a parent can't
                    mix sub-items and choice options. */}
                {editable && !isOption && !hasPlainChildren && (
                  <Act
                    icon="nc-icon nc-vector"
                    label={
                      choiceGroup
                        ? strings.add_choice_option
                        : strings.add_choices
                    }
                    onClick={() => onAddOption(item)}
                  />
                )}
                {editable && (
                  <Act
                    icon="nc-icon nc-ruler-pencil"
                    label={strings.edit}
                    onClick={() => onEdit(item)}
                  />
                )}
                {editable && (
                  <Act
                    icon="nc-icon nc-simple-remove"
                    label={strings.delete}
                    tone="danger"
                    onClick={() => onRemove(item)}
                  />
                )}
              </div>
            </div>

            <div className="wo-item__title">{item.name}</div>
          </div>

          {item.description && (
            <div className="wo-item__desc">{item.description}</div>
          )}

          {/* sub-items and options carry a warranty of their own */}
          <TaskStrip item={item} status={status} parts={this.props.parts} />

          {item.customerNote && (
            <div className="wo-item__note">
              <i className="nc-icon nc-chat-round" />
              <span>
                <strong>{strings.client_note}: </strong>
                {item.customerNote}
              </span>
            </div>
          )}

          {/* Photos stay in the row — they are the evidence for this finding,
              and unlike a thread they are looked at alongside it. */}
          {panel === "photos" && (
            <div className="wo-photos">
              {PHOTO_KINDS.map(({ kind, label }) => (
                <PhotoSlot
                  key={kind}
                  kind={kind}
                  label={label()}
                  photo={photos.find((photo) => photo.kind === kind)}
                  canWrite={photoable}
                  busy={busy}
                  onUpload={(pickedKind, file) =>
                    onUploadPhoto(item, pickedKind, file)
                  }
                  onRemove={(photo) => onRemovePhoto(item, photo)}
                />
              ))}
            </div>
          )}
        </div>

        {children.map((child) => (
          <ItemNode
            {...this.props}
            key={child.id}
            item={child}
            depth={depth + 1}
            isOption={choiceGroup}
          />
        ))}
      </React.Fragment>
    );
  }
}

const TypeGroup = ({ type, items, ...rest }) => {
  const group = items.filter(
    (item) => (item.type ?? ItemType.OPTIONAL) === type
  );
  if (group.length === 0) return null;
  const meta = itemTypeMeta(type);

  return (
    <div className={`wo-group is-${meta.tone}`}>
      <div className="wo-group__head">
        <i className={meta.icon} />
        <span>{meta.label}</span>
        <span className="wo-group__count">{group.length}</span>
      </div>
      {group.map((item) => (
        <ItemNode key={item.id} item={item} depth={0} isOption={false} {...rest} />
      ))}
    </div>
  );
};

const WorkOrderItems = ({
  items = [],
  status,
  canWrite,
  onAddItem,
  onPickServices,
  currencySymbol,
  ...rest
}) => {
  const editable = canWrite && canEditItems(status);
  const total = totalEstimated(items);

  return (
    <div className="wo-card">
      <div className="wo-card__head">
        <h6 className="wo-card__title">
          <i className="nc-icon nc-settings-tool-66" />
          {strings.work_items}
        </h6>
        {editable && (
          <div className="wo-card__spacer">
            <button type="button" className="wo-link" onClick={onAddItem}>
              <i className="nc-icon nc-simple-add" /> {strings.add_item}
            </button>{" "}
            <button type="button" className="wo-link" onClick={onPickServices}>
              <i className="nc-icon nc-settings-gear-65" />{" "}
              {strings.from_garage_services}
            </button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="wo-empty">
          <i className="nc-icon nc-box" />
          {strings.no_items_yet}
        </div>
      ) : (
        <>
          {[ItemType.PRIORITY, ItemType.OPTIONAL, ItemType.SERVICE].map((type) => (
            <TypeGroup
              key={type}
              type={type}
              items={items}
              status={status}
              canWrite={canWrite}
              currencySymbol={currencySymbol}
              {...rest}
            />
          ))}
          {total > 0 && (
            <div className="wo-total">
              <i className="nc-icon nc-money-coins" />
              <span>
                {status === WorkOrderStatus.DELIVERED
                  ? strings.total
                  : strings.estimated_total}
              </span>
              <span className="wo-total__value">
                {money(total, currencySymbol)}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WorkOrderItems;
